// Pusher → TanStack Query cache bridge for the talent SPA (mirrors web's
// agent-stream-dispatcher.ts). Folds one de-duped talent event into the Query
// cache, replacing the old "bump a tick → re-GET everything" pulse:
//   • seller.message carries the full body (10KB-safe), so we setQueryData-append
//     it to the open thread's detail and patch the inbox row — no refetch.
//   • seller.thread.update is a state-only nudge (no offer/messages), so we patch
//     the inbox row's state/has_offer and INVALIDATE the detail to re-GET the
//     authoritative offer.
// Idempotent — safe to re-fold on a StrictMode remount or Pusher redelivery
// (doctrine 1.3). Reads the active talent from the store via getState() for keys,
// exactly as web's dispatcher reads currentProjectId.

import { assertExhaustive } from "@recruiter/shared/types";
import type { SellerMessageDTO } from "@recruiter/shared";

import { log } from "./log";
import { queryClient } from "./query-client";
import { sellerKeys, updateThreadDetailCache, updateThreadListCache } from "./queries/seller";
import { affectedThreadId, type SellerLiveEvent } from "./seller-stream";
import { useSellerStore } from "./store/seller-store";

const s = useSellerStore.getState;

// seller.message carries no message id; synthesize a stable one from turn_id:seq so
// the append is idempotent (a re-fold finds it already present) and React keys stay
// stable. A later authoritative GET replaces the whole messages array, so the
// synthetic id resolves to the real server id on the next detail read.
function syntheticId(ev: { turn_id: string; seq: number }): string {
  return `ev-${ev.turn_id}-${ev.seq}`;
}

export function applySellerEvent(ev: SellerLiveEvent): void {
  const sellerId = s().activeSellerId;
  if (!sellerId) return; // no identity → nothing to key
  const threadId = affectedThreadId(ev);

  if (ev.type === "seller.message") {
    const id = syntheticId(ev);
    const message: SellerMessageDTO = {
      id,
      body: ev.body,
      sender: ev.sender, // "concierge" | "talent" widens to the DTO's `string`
      sent_at: ev.sent_at,
    };
    // 1) Append to the open thread's detail (iff cached). Idempotent by id.
    updateThreadDetailCache(sellerId, threadId, (current) =>
      current.messages.some((m) => m.id === id)
        ? current
        : { ...current, messages: [...current.messages, message] },
    );
    // 2) Patch the inbox row's preview + move it to the front (iff the list is cached
    //    and the row exists — never fabricate a row for an unknown thread).
    updateThreadListCache(sellerId, (current) => {
      const row = current.threads.find((t) => t.thread_id === threadId);
      if (!row) return current;
      const updated = { ...row, last_message: ev.body, last_message_at: ev.sent_at };
      return {
        ...current,
        threads: [updated, ...current.threads.filter((t) => t.thread_id !== threadId)],
      };
    });
    return;
  }

  if (ev.type === "seller.thread.update") {
    // 1) Patch the inbox row's state/has_offer (both are in the event).
    updateThreadListCache(sellerId, (current) => ({
      ...current,
      threads: current.threads.map((t) =>
        t.thread_id === threadId ? { ...t, state: ev.state, has_offer: ev.has_offer } : t,
      ),
    }));
    // 2) Invalidate the detail so the authoritative offer/messages re-GET (the
    //    10KB-safe event omits them). A no-op refetch unless the thread is open.
    void queryClient.invalidateQueries({ queryKey: sellerKeys.thread(sellerId, threadId) });
    return;
  }

  // talent events are an OPEN wire boundary — a newer backend may publish a type this
  // build doesn't model. Log + skip (never throw in the fold); assertExhaustive makes
  // adding a modeled SellerLiveEvent variant without a branch here fail typecheck.
  log.warn("seller-stream", "ignoring unknown event type", {
    type: (ev as { type?: string }).type,
  });
  assertExhaustive(ev);
}
